import * as React from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@aitoken/ui/components/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@aitoken/ui/components/sheet';
import { SiteTag } from '@aitoken/ui/components/site';
import { cn } from '@aitoken/ui/lib/utils';
import { Wordmark } from '@/components/wordmark';
import { getLocaleTag, useLocale } from '@/i18n';
import { LanguageSwitcher } from '@/i18n/language-switcher';
import { useCommonT } from '@/i18n/messages/common';
import { usePublicT } from '@/i18n/messages/public';
import { meQuery, useSite } from '@/lib/queries';

const NAV = [
  { to: '/models', key: 'nav.models' },
  { to: '/docs', key: 'nav.docs' },
] as const;

const PAGE = 'mx-auto w-full max-w-[1440px] page-x';

function SiteSwitcher() {
  const site = useSite();
  const t = usePublicT();
  const ct = useCommonT();
  const other = site.site_id === 'cn' ? 'global' : 'cn';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="rounded-[4px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        aria-label={t('site.switch', { site: ct(site.site_id === 'cn' ? 'site.cn' : 'site.global') })}
      >
        <SiteTag
          site={site.site_id}
          className="h-7 rounded-[4px] border-transparent bg-white/10 text-[13px] [&>span:last-child]:text-white"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal text-muted-foreground">
          {t('site.isolated')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem disabled>
            <SiteTag site={site.site_id} />
            <span className="ml-auto text-xs text-muted-foreground">{t('site.current')}</span>
          </DropdownMenuItem>
          {site.other_site_url ? (
            <DropdownMenuItem asChild>
              <a href={site.other_site_url}>
                <SiteTag site={other} />
                <ArrowUpRight className="ml-auto" aria-hidden />
              </a>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled>
              <SiteTag site={other} />
              <span className="ml-auto text-xs text-muted-foreground">{t('site.unavailable')}</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NAV_LINK =
  'rounded-[4px] px-3 py-2 text-[15px] whitespace-nowrap text-[#d5d1c9] transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-gold';
const SOLID =
  'press inline-flex h-9 items-center rounded-[4px] bg-gold px-4 text-[15px] font-semibold whitespace-nowrap text-ink hover:bg-[#dcc69a] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold';

export function PublicHeader() {
  const t = usePublicT();
  const { data: me } = useQuery(meQuery);
  const site = useSite();
  const [open, setOpen] = React.useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  React.useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="dark field-ink sticky top-0 z-40 w-full bg-ink text-white">
      <div className={cn(PAGE, 'flex h-16 items-center gap-6')}>
        <Link
          to="/"
          className="rounded-[4px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold"
          aria-label={t('nav.home')}
        >
          <Wordmark />
        </Link>
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <nav aria-label={t('nav.main')} className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={NAV_LINK}
                activeProps={{ className: 'text-white', 'aria-current': 'page' }}
              >
                {t(item.key)}
              </Link>
            ))}
            <a href="#contact" className={NAV_LINK}>
              {t('nav.contact')}
            </a>
          </nav>
          <div className="hidden sm:block">
            <SiteSwitcher />
          </div>
          <LanguageSwitcher
            compact
            className="rounded-[4px] px-2.5 text-[#d5d1c9] hover:text-white lg:hidden"
          />
          <LanguageSwitcher className="hidden rounded-[4px] text-[15px] text-[#d5d1c9] hover:text-white lg:inline-flex" />
          {me ? (
            <Link to="/console" className={SOLID}>
              {t('nav.console')}
            </Link>
          ) : (
            <>
              <Link to="/login" className={cn(NAV_LINK, 'hidden sm:inline-flex')}>
                {t('nav.login')}
              </Link>
              <Link to="/register" className={SOLID}>
                {t('nav.register')}
              </Link>
            </>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex size-10 items-center justify-center rounded-[4px] text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-gold lg:hidden"
                aria-label={t('nav.openMenu')}
              >
                <Menu className="size-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full max-w-xs">
              <SheetHeader>
                <SheetTitle>
                  <Wordmark tone="dark" />
                </SheetTitle>
                <SheetDescription className="sr-only">{t('nav.siteNav')}</SheetDescription>
              </SheetHeader>
              <nav aria-label={t('nav.mobile')} className="flex flex-col gap-1 px-4">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="rounded-[4px] px-3 py-2.5 text-base text-muted-foreground hover:bg-accent hover:text-foreground"
                    activeProps={{
                      className: 'text-foreground font-medium bg-accent',
                      'aria-current': 'page',
                    }}
                  >
                    {t(item.key)}
                  </Link>
                ))}
                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  className="rounded-[4px] px-3 py-2.5 text-base text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  {t('nav.contact')}
                </a>
                {me ? null : (
                  <Link to="/login" className="rounded-[4px] px-3 py-2.5 text-base hover:bg-accent">
                    {t('nav.login')}
                  </Link>
                )}
                <div className="px-3 py-3">
                  <SiteTag site={site.site_id} />
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function useClock(timeZone: string) {
  const locale = useLocale();
  const fmt = React.useMemo(() => {
    const tag = getLocaleTag();
    try {
      return new Intl.DateTimeFormat(tag, {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return new Intl.DateTimeFormat(tag, { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZone, locale]);
  const [now, setNow] = React.useState(() => fmt.format(new Date()));
  React.useEffect(() => {
    setNow(fmt.format(new Date()));
    const id = window.setInterval(() => setNow(fmt.format(new Date())), 30_000);
    return () => window.clearInterval(id);
  }, [fmt]);
  return now;
}

const CITY = {
  'Asia/Shanghai': 'footer.city.shanghai',
  'Asia/Singapore': 'footer.city.singapore',
  UTC: 'footer.city.utc',
} as const;

/** 页脚：一块“看板”的最后两行，每格都填满。 */
export function PublicFooter() {
  const t = usePublicT();
  const site = useSite();
  const { data: me } = useQuery(meQuery);
  const { contact } = site;
  const clock = useClock(site.timezone);
  const tile = 'flex flex-col justify-between gap-6 rounded-[4px] bg-frame p-6 text-ink';
  const contactHref = contact.email ? `mailto:${contact.email}` : '#contact';
  const city = CITY[site.timezone as keyof typeof CITY];

  return (
    <footer id="contact" className="field-white bg-white p-2 sm:p-3" aria-labelledby="footer-title">
      <h2 id="footer-title" className="sr-only">
        {t('footer.title')}
      </h2>
      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 lg:grid-cols-12">
        {/* 上排 */}
        <div className={cn(tile, 'order-6 col-span-2 lg:order-none lg:col-span-4 lg:min-h-[220px]')}>
          <p className="text-[24px] leading-snug font-medium">{t('footer.tagline')}</p>
          <div className="flex flex-col gap-1 text-[13px] text-[#6b645a]">
            {site.announcement ? (
              <span>
                {t('footer.announcement')}
                <span data-i18n-ignore>{site.announcement}</span>
              </span>
            ) : null}
            <span>
              © {new Date().getFullYear()} <span data-i18n-ignore>{site.name}</span>
            </span>
          </div>
        </div>
        <nav
          aria-label={t('footer.nav')}
          className={cn(tile, 'order-3 justify-start gap-2 lg:order-none lg:col-span-3 lg:min-h-[220px]')}
        >
          {NAV.map((l) => (
            <Link key={l.to} to={l.to} className="w-fit text-base hover:underline hover:underline-offset-4">
              {t(l.key)}
            </Link>
          ))}
          <Link
            to="/docs/$slug"
            params={{ slug: 'changelog' }}
            className="w-fit text-base hover:underline hover:underline-offset-4"
          >
            {t('footer.changelog')}
          </Link>
          <Link to="/legal/$doc" params={{ doc: 'terms' }} className="w-fit text-base hover:underline hover:underline-offset-4">
            {t('footer.terms')}
          </Link>
          <Link to="/legal/$doc" params={{ doc: 'privacy' }} className="w-fit text-base hover:underline hover:underline-offset-4">
            {t('footer.privacy')}
          </Link>
          <Link
            to={me ? '/console' : '/login'}
            className="w-fit text-base hover:underline hover:underline-offset-4"
          >
            {me ? t('nav.console') : t('nav.login')}
          </Link>
          {site.other_site_url ? (
            <a
              href={site.other_site_url}
              className="w-fit text-base hover:underline hover:underline-offset-4"
            >
              {t('footer.otherSite', { site: site.site_id === 'cn' ? 'global' : 'cn' })}
            </a>
          ) : null}
          <LanguageSwitcher className="mt-auto h-auto w-fit gap-2 rounded-[4px] px-0 py-1 text-base text-[#4a453d] hover:text-ink focus-visible:ring-ink" />
        </nav>
        <div className={cn(tile, 'order-5 col-span-2 lg:order-none lg:col-span-2 lg:min-h-[220px]')}>
          <span className="text-base">{city ? t(city) : site.timezone}</span>
          <span className="flex flex-col">
            <span className="display text-[40px] tabular-nums">{clock}</span>
            <span className="text-[13px] text-[#6b645a]">{t('footer.tzNote')}</span>
          </span>
        </div>
        <div className={cn(tile, 'order-4 lg:order-none lg:col-span-3 lg:min-h-[220px]')}>
          <span className="text-base">{t('footer.contact')}</span>
          <span className="flex min-w-0 flex-col gap-1">
            {contact.wechat ? (
              <span className="text-[15px] text-[#4a453d]">{t('footer.wechat', { id: contact.wechat })}</span>
            ) : null}
            {contact.email ? (
              <a
                href={`mailto:${contact.email}`}
                className="truncate text-[18px] font-medium underline underline-offset-4 hover:text-[#8a6a32]"
              >
                {contact.email}
              </a>
            ) : (
              <span className="text-[15px] text-[#4a453d]">{t('footer.noContact')}</span>
            )}
          </span>
        </div>

        {/* 下排 */}
        <div className="order-1 col-span-2 flex min-h-[160px] items-center justify-center rounded-[4px] bg-ink lg:order-none lg:col-span-8 lg:min-h-[320px]">
          <span className="display text-[13vw] lowercase lg:text-[6.6vw]" aria-hidden>
            <span className="text-gold">aitoken</span>
            <span className="text-[#6b645a]">.tools</span>
          </span>
        </div>
        <a
          href={contactHref}
          className="press group order-2 col-span-2 flex min-h-[160px] flex-col justify-between rounded-[4px] bg-gold p-6 text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink lg:order-none lg:col-span-4 lg:min-h-[320px]"
        >
          <span className="text-[28px] leading-tight font-semibold">
            {t('footer.cta.a')}
            <br />
            {t('footer.cta.b')}
          </span>
          <svg
            viewBox="0 0 48 48"
            className="size-12 self-end transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1"
            fill="none"
            stroke="currentColor"
            strokeWidth={4}
            strokeLinecap="square"
            aria-hidden
          >
            <path d="M12 36 36 12M16 12h20v20" />
          </svg>
        </a>
      </div>
    </footer>
  );
}

export function PublicLayout() {
  const t = usePublicT();
  return (
    <div className="flex min-h-svh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:text-sm"
      >
        {t('nav.skip')}
      </a>
      <PublicHeader />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
}
